import styled from "styled-components";
import {
  Checkbox,
  IconButton,
  TooltipHost,
  Icon,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import React, { useContext, useRef, useState, useEffect } from "react";
import { ColumnOptionsContext } from "./ColumnOptions";
import { FilterDropdown } from "./FilterDropdown";
import { Column } from "./types";
import { isColumnFiltered } from "./utils";

type Props = {
  column: Column;
  isDragging?: boolean;
  dragHandleProps?: any;
};

export const ColumnListItem = ({
  column,
  isDragging,
  dragHandleProps,
}: Props) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const refInnerContainer = useRef<HTMLDivElement>(null);
  const refRoot = useRef<HTMLDivElement>(null);
  const filterButtonRef = useRef<HTMLDivElement>(null);
  const { onToggleColumnVisibility, strings } =
    useContext(ColumnOptionsContext);

  const isFiltered = column.filter && isColumnFiltered(column.filter);
  const lastCalloutInteractionRef = useRef<number>(0);

  // Clear loading state once dropdown is open
  useEffect(() => {
    if (isDropdownOpen) {
      setIsFilterLoading(false);
    }
  }, [isDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const targetElement = target as Element;

      // Don't close if clicking inside the root element
      if (refRoot.current && refRoot.current.contains(target)) {
        return;
      }

      // Don't close if clicking on any Fluent UI callout/layer/portal
      const fluentUIPortal = targetElement.closest(
        ".ms-Callout, .ms-Layer, .ms-DatePicker, .ms-DatePicker-event, .ms-Dropdown-callout, " +
          "[role='dialog'], [role='listbox'], [role='menu']"
      );
      if (fluentUIPortal) {
        lastCalloutInteractionRef.current = Date.now();
        return;
      }

      // Don't close immediately after interacting with a portal
      if (Date.now() - lastCalloutInteractionRef.current < 150) {
        return;
      }

      setIsDropdownOpen(false);
      if (column.onDismissCallout) column.onDismissCallout();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen, column]);

  let width = 0;
  if (refInnerContainer.current) {
    width = refInnerContainer.current.getBoundingClientRect().width;
  }

  return (
    <Root ref={refRoot} isDragging={isDragging}>
      <InnerContainer
        ref={refInnerContainer}
        isActive={isDropdownOpen || isDragging}
      >
        <TooltipHost content={strings.dragHandleTitle}>
          <DragHandle
            {...dragHandleProps}
            isDisabled={column.isFixed}
            tabIndex={column.isFixed ? -1 : 0}
          >
            <Icon
              iconName="GripperDotsVertical"
              styles={{
                root: {
                  fontSize: 16,
                  color: column.isFixed ? "#a19f9d" : undefined,
                },
              }}
            />
          </DragHandle>
        </TooltipHost>

        <LabelWrapper>
          <TooltipHost content={column.label}>
            <Label>{column.label}</Label>
          </TooltipHost>
        </LabelWrapper>

        <RightSlots>
          <FilterSlot ref={filterButtonRef}>
            {!!column.filter && (
              isFilterLoading ? (
                <Spinner size={SpinnerSize.xSmall} styles={{ root: { width: 24, height: 24 } }} />
              ) : (
                <TooltipHost content={isFiltered ? "Filter active" : "Add filter"}>
                  <IconButton
                    iconProps={{
                      iconName: isFiltered ? "FilterSolid" : "Filter",
                    }}
                    onClick={() => {
                      if (!isDropdownOpen) {
                        setIsFilterLoading(true);
                        if (column.onOpenCallout) column.onOpenCallout();
                        // Defer opening to next frame so spinner renders first
                        requestAnimationFrame(() => {
                          setIsDropdownOpen(true);
                        });
                      } else {
                        if (column.onDismissCallout) column.onDismissCallout();
                        setIsDropdownOpen(false);
                      }
                    }}
                    styles={{
                      root: {
                        width: 24,
                        height: 24,
                      },
                      icon: {
                        fontSize: 14,
                      },
                    }}
                  />
                </TooltipHost>
              )
            )}
          </FilterSlot>
          <Checkbox
            checked={column.isVisible}
            onChange={() => onToggleColumnVisibility(column.key)}
            title={strings.visibilityCheckboxTitle}
            disabled={column.isFixed || column.isAlwaysVisible}
          />
        </RightSlots>
      </InnerContainer>
      {isDropdownOpen && (
        <FilterDropdown
          column={column}
          width={width}
          isOpen={isDropdownOpen}
          close={() => {
            if (column.onDismissCallout) column.onDismissCallout();
            setIsDropdownOpen(false);
          }}
          target={filterButtonRef}
        />
      )}
    </Root>
  );
};

const Root = styled.div<{ isDragging?: boolean }>`
  margin-bottom: 2px;
  position: relative;
  z-index: ${(props) => (props.isDragging ? 9999 : 1)};
`;

const InnerContainer = styled.div<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 0;
  background: ${(props) =>
    props.isActive ? props.theme.palette.neutralLighter : "transparent"};
  border-radius: 4px;
`;

const DragHandle = styled.div<{ isDisabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  cursor: ${(props) => (props.isDisabled ? "not-allowed" : "grab")};
  opacity: ${(props) => (props.isDisabled ? 0.4 : 1)};
`;

const LabelWrapper = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const Label = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  padding: 0 4px;
`;

const RightSlots = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  flex-shrink: 0;
`;

const FilterSlot = styled.div`
  width: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
